// src/components/Login.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";
import { FaKey, FaPhoneAlt, FaEyeSlash, FaEye, FaWhatsapp, FaSms } from "react-icons/fa";
import image from "../image/login-model.png";
import bowser from "bowser";
import FingerprintJS from "@fingerprintjs/fingerprintjs"; // For browser fingerprinting
import LeftPanel from "./LeftPanel";
import apiClient from "../common/apiClient";
import decryptResponse from "../common/decrypt";
import ProBrokerFooter from "./ProBrokerFooter";
import { useUserProfile } from "../contexts/UserProfileContext";
import { postLoginDestination, saveLoginSession } from "../common/authSession";

const OTP_CHANNELS = [
  { value: "whatsapp", label: "WhatsApp", Icon: FaWhatsapp, color: "text-[#25D366]" },
  { value: "sms", label: "SMS", Icon: FaSms, color: "text-[#503691]" },
];

// WhatsApp / SMS picker shared by the login form and the OTP screen.
const OtpChannelToggle = ({ value, onChange, disabled = false }) => (
  <div className="grid grid-cols-2 gap-3 mb-4">
    {OTP_CHANNELS.map(({ value: channel, label, Icon, color }) => (
      <button
        key={channel}
        type="button"
        onClick={() => onChange(channel)}
        disabled={disabled}
        aria-pressed={value === channel}
        className={`flex items-center justify-center gap-2 py-2.5 rounded-md border font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
          value === channel
            ? "border-[#503691] bg-[#503691]/10 text-[#503691]"
            : "border-gray-300 text-gray-700"
        }`}
      >
        <Icon className={color} /> {label}
      </button>
    ))}
  </div>
);

const Login = () => {
  const { fetchUserProfile } = useUserProfile();
  const [number, setNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false); // Loading state
  const [acceptedTerms, setAcceptedTerms] = useState(true); // State for terms and conditions
  // Login OTP step: channel the code goes out on, and the OTP screen state.
  const [otpChannel, setOtpChannel] = useState("whatsapp");
  // Channel picked on the OTP screen for the next resend.
  const [resendChannel, setResendChannel] = useState("whatsapp");
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [maskedNumber, setMaskedNumber] = useState("");
  const otpInputRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const id = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);

  useEffect(() => {
    const userId = Cookies.get("userId");
    const token = Cookies.get("token");
    if (userId && token) {
      navigate(postLoginDestination());
    }
  }, [navigate]);

  // Function to gather device details
  const getDeviceDetails = () => {
    const browser = bowser.getParser(window.navigator.userAgent);
    const info = browser.getResult();

    return {
      browser: info.browser.name,
      browserVersion: info.browser.version,
      os: info.os.name,
      osVersion: info.os.version,
      platform: info.platform.type,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      userAgent: navigator.userAgent,
    };
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState); // Toggle the password visibility state
  };

  // Function to get browser fingerprint (unique ID for the browser)
  const getFingerprint = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId; // Unique ID for browser
  };

  // Function to get the user's IP address
  const getIPAddress = async () => {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      return response.data.ip; // Returns the IP address of the user
    } catch (error) {
      console.error("Unable to fetch IP address", error);
      return null;
    }
  };

  const handleNumberChange = (e) => {
    setNumber(e.target.value);
    if (error) setError(""); // Clear error when user starts typing
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError(""); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true); // Set loading to true when submission starts

    // Validate form fields
    if (!number) {
      setError(
        "Please enter your phone number to access your PRObroker account."
      );
      setLoading(false); // Set loading to false if validation fails
      return;
    }

    if (!acceptedTerms) {
      setError("Please accept the terms and conditions to proceed.");
      setLoading(false);
      return;
    }

    // Validate contact number format
    const contactPattern = /^[0-9]{10}$/; // Example pattern for 10-digit numbers
    if (!contactPattern.test(number)) {
      setError(
        "Please enter a valid 10-digit phone number to access your PRObroker account."
      );
      setLoading(false); // Set loading to false if validation fails
      return;
    }
    // Password field is commented out, so its length check is disabled too.
    // if (password.length < 6) {
    //   setError("Password must be at least 6 characters long.");
    //   setLoading(false);
    //   return;
    // }
    // Step 1: an OTP goes out on the chosen channel.
    await sendLoginOtp(otpChannel, setError);
    setLoading(false);
  };

  const errorFrom = (response, fallback) => {
    const message =
      typeof response.error === "string"
        ? response.error
        : response.error?.message || response.data?.message;
    return message || fallback;
  };

  // Sends (or resends) the login OTP. `reportError` is where a failure shows:
  // the login form's error box, or the OTP screen's.
  const sendLoginOtp = async (channel, reportError) => {
    const response = await apiClient(
      "post",
      "/cjidnvij/ceksfbuebijn/user/v2/login/send-otp/dljcnji/cekbjid",
      { number, channel }
    );

    if (!response.success) {
      reportError(errorFrom(response, "Could not send OTP. Please try again."));
      return false;
    }

    setOtpChannel(channel);
    setResendChannel(channel);
    setMaskedNumber(response.data?.number || "");
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    setResendTimer(response.data?.resendAfterSeconds || 60);
    setShowOtpScreen(true);
    setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    return true;
  };

  const handleResendOtp = async (channel) => {
    if (resendTimer > 0 || otpLoading) return;
    setOtpLoading(true);
    await sendLoginOtp(channel, setOtpError);
    setOtpLoading(false);
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setOtpError("");
    if (value && index < 5) otpInputRefs.current[index + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pasted.length === 6) {
      e.preventDefault();
      setOtp(pasted.split(""));
      setOtpError("");
      otpInputRefs.current[5]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Step 2: the OTP is verified and the backend issues the session.
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP.");
      return;
    }

    setOtpLoading(true);
    setOtpError("");
    try {
      const deviceId = await getFingerprint();
      const response = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/v2/login/verify-otp/dljcnji/cekbjid",
        { number, otp: otpString, deviceId }
      );

      if (!response.success) {
        setOtpError(errorFrom(response, "Invalid OTP. Please try again."));
        return;
      }

      await completeLogin(response);
    } catch (err) {
      console.error("Verify login OTP error:", err);
      setOtpError("An unexpected error occurred. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // Saves the session the backend returned and moves into the app.
  const completeLogin = async (response) => {
    try {
      if (response.data && (response.data.success === true || response.data.status === "success")) {
        // Save token, user details and device, shared with the signup auto-login.
        await saveLoginSession(response.data.data, { fetchUserProfile });

        // Navigate to home
        navigate(postLoginDestination());
        window.location.reload();
      } else {
        setOtpError(errorFrom(response, "Login failed. Please try again."));
      }
    } catch (error) {
      console.error("Error completing login:", error);
      setOtpError("An unexpected error occurred. Please try again later.");
    }
  };

  return (
    <>
    <div className="flex h-screen w-full overflow-hidden">
      {/* Left panel with animations and graphics */}
      <LeftPanel />

      {showOtpScreen ? (
      <form
        onSubmit={handleVerifyOtp}
        className="mx-4 lg:mx-28 mb-28 flex items-center"
      >
        <div className="flex-1 max-w-lg rounded-lg mt-10">
          <p className="text-3xl font-bold mb-2">Verify OTP</p>
          <p className="text-gray-600 mb-8 flex items-center gap-2">
            {otpChannel === "whatsapp" ? (
              <FaWhatsapp className="text-[#25D366]" />
            ) : (
              <FaSms className="text-[#503691]" />
            )}
            Enter the 6-digit code sent on {otpChannel === "whatsapp" ? "WhatsApp" : "SMS"} to{" "}
            <span className="font-semibold">{maskedNumber || number}</span>
          </p>

          <div className="flex justify-between gap-2 mb-4" onPaste={handleOtpPaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (otpInputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? "one-time-code" : "off"}
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                className="w-12 h-12 text-center text-xl border border-gray-300 rounded-md focus:border-[#503691] focus:outline-none"
              />
            ))}
          </div>

          {otpError && (
            <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 text-center mb-4 shadow-sm font-medium">
              {otpError}
            </div>
          )}

          <button
            type="submit"
            className="w-full h-11 bg-[#503691] text-white rounded-full font-semibold hover:bg-[#5b54e6] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={otpLoading}
          >
            {otpLoading ? "Verifying..." : "Verify & Login"}
          </button>

          <div className="mt-6">
            <p className="mb-2 font-medium text-gray-700">Resend OTP on</p>
            <OtpChannelToggle
              value={resendChannel}
              onChange={setResendChannel}
              disabled={otpLoading}
            />
          </div>

          <div className="text-center text-sm text-gray-700">
            {resendTimer > 0 ? (
              <p>Resend OTP in {resendTimer}s</p>
            ) : (
              <button
                type="button"
                onClick={() => handleResendOtp(resendChannel)}
                disabled={otpLoading}
                className="text-[#503691] font-semibold hover:underline disabled:opacity-50"
              >
                Resend OTP via {resendChannel === "whatsapp" ? "WhatsApp" : "SMS"}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setShowOtpScreen(false);
                setOtpError("");
              }}
              className="mt-3 text-gray-500 hover:underline"
            >
              Change number
            </button>
          </div>
        </div>
      </form>
      ) : (
      <form
        onSubmit={handleSubmit}
        className="mx-4 lg:mx-28 mb-28 flex items-center "
      >
        <div className="flex-1 max-w-lg  rounded-lg mt-10">
          {/* <p className="text-4xl text-gray-800 mb-1">Welcome to</p>
          <p className="text-5xl font-bold text-[#503691] mb-4">PRObroker</p> */}
          <p className="text-3xl  font-bold mb-8">Login</p>
          <div className="relative mb-4">
            <FaPhoneAlt className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
            <input
              type="text"
              placeholder="Enter Your Contact Number"
              value={number.slice(0, 10)}
              onChange={handleNumberChange}
              className="w-full px-10 py-3 border border-gray-300 rounded-md no-spinner"
              required
            />
          </div>
          {/* <div className="relative mb-4">
            <FaKey className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
            <input
              type="password"
              placeholder="Enter Your Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-10 py-3 border border-gray-300 rounded-md"
              required
            />
          </div> */}
          {/* Password field commented out - login is OTP only for now
          <div className="relative mb-4">
            <FaKey className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter Your Password"
              value={password}
              onChange={handlePasswordChange}
              className="w-full px-10 py-3 border border-gray-300 rounded-md"
              required
            />
            <button
              type="button"
              className="absolute top-1/2 right-4 transform -translate-y-1/2 text-[#503691] focus:outline-none"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          */}
          <p className="mb-2 font-medium text-gray-700">Get login OTP on</p>
          <OtpChannelToggle value={otpChannel} onChange={setOtpChannel} />
          {/* Forgot Password link commented out - login is OTP only for now
          <div className="text-right mb-4">
            <Link to="/forgetpassword" size="sm" className="text-[#503691] font-semibold hover:underline">
              Forgot Password?
            </Link>
          </div>
          */}
          {error && (
            <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 text-center mb-4 shadow-sm">
              <div className="flex items-center justify-center gap-2">
                <svg
                  className="w-5 h-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">{error}</span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 py-3">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="h-7 w-7 rounded border-gray-300 text-[#503691] focus:ring-[#503691] cursor-pointer"
            />
            <label htmlFor="terms" className="lg:text-[15px] text-[13px] text-gray-700 text-center">
              By clicking on login button you're accepting our{" "}
              <Link to="/terms-and-condition" className="text-blue-500 hover:underline">
                Terms and Conditions
              </Link> {" "}
              and{" "}
              <Link to="/privacy-policy" className="text-blue-500 hover:underline">
                Privacy Policy
              </Link>
            </label>
          </div>
          <button
            type="submit"
            className="w-full h-11 bg-[#503691] text-white rounded-full font-semibold hover:bg-[#5b54e6] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !acceptedTerms}
          >
            {loading ? "Sending OTP..." : "Get OTP"}
          </button>
          {loading && <div className="mt-2 text-center">Loading...</div>}
          <div className="text-center mt-4">
            <p>
              Not a member?{" "}
              <Link to="/signup" className="text-[#503691] font-semibold">
                Signup
              </Link>
            </p>
          </div>
        </div>
      </form>
      )}
    </div>
    {/* <ProBrokerFooter /> */}
    </>
  );
};

export default Login;
